import * as React from 'react'
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { postJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import { stripePromise, stripePublishableKey } from '@/lib/stripe'
import {
  OfflinePaymentFields,
  type PaymentAllocationQuote,
} from '@/pages/RecordPaymentAllocationFields'
import type {
  Invoice,
  InvoiceResponse,
  PaymentForm,
  PhonePaymentForm,
} from '@/pages/InvoicesPage.types'

export { stripePublishableKey }

export function RecordPaymentDialog({
  invoice,
  currentAssociationDate,
  pending,
  error,
  phonePending,
  phoneError,
  onClose,
  onSubmit,
  onSubmitPhone,
}: {
  invoice: Invoice
  currentAssociationDate?: string
  pending: boolean
  error: Error | null
  phonePending: boolean
  phoneError: Error | null
  onClose: () => void
  onSubmit: (form: PaymentForm) => void
  onSubmitPhone: (form: PhonePaymentForm) => void
}) {
  const [form, setForm] = React.useState<PaymentForm>(() => ({
    amount: '',
    method: 'cash',
    paidOn: currentAssociationDate ?? '',
    reference: '',
    idempotencyKey: paymentIdempotencyKey(),
    allocations: [],
  }))
  const [paymentKind, setPaymentKind] = React.useState('offline')
  const [allocationQuote, setAllocationQuote] = React.useState<PaymentAllocationQuote | null>(null)

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <form
          onSubmit={(event) => {
            event.preventDefault()
            onSubmit(form)
          }}
        >
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>{invoice.contact_name}</DialogDescription>
          </DialogHeader>

          <PaymentKindTabs
            invoice={invoice}
            form={form}
            onFormChange={setForm}
            paymentKind={paymentKind}
            onPaymentKindChange={setPaymentKind}
            error={error}
            phonePending={phonePending}
            phoneError={phoneError}
            onAllocationQuoteChange={setAllocationQuote}
            onSubmitPhone={onSubmitPhone}
          />

          {paymentKind === 'offline' && (
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                aria-label="Record Payment"
                disabled={pending || !allocationQuote}
              >
                {pending
                  ? 'Recording...'
                  : allocationQuote
                    ? `Record ${allocationQuote.payment}`
                    : 'Record Payment'}
              </Button>
            </DialogFooter>
          )}
        </form>
      </DialogContent>
    </Dialog>
  )
}

function PaymentKindTabs({
  invoice,
  form,
  onFormChange,
  paymentKind,
  onPaymentKindChange,
  error,
  phonePending,
  phoneError,
  onAllocationQuoteChange,
  onSubmitPhone,
}: {
  invoice: Invoice
  form: PaymentForm
  onFormChange: (form: PaymentForm) => void
  paymentKind: string
  onPaymentKindChange: (kind: string) => void
  error: Error | null
  phonePending: boolean
  phoneError: Error | null
  onAllocationQuoteChange: (quote: PaymentAllocationQuote | null) => void
  onSubmitPhone: (form: PhonePaymentForm) => void
}) {
  return (
    <Tabs value={paymentKind} onValueChange={onPaymentKindChange}>
      <TabsList>
        <TabsTrigger value="offline">Offline Payment</TabsTrigger>
        {invoice.phone_payment_available && (
          <TabsTrigger value="phone" onClick={() => onPaymentKindChange('phone')}>
            Charge card by phone
          </TabsTrigger>
        )}
      </TabsList>
      <TabsContent value="offline" className="space-y-4">
        <OfflinePaymentFields
          invoice={invoice}
          form={form}
          onChange={onFormChange}
          error={error}
          onQuoteChange={onAllocationQuoteChange}
        />
        {!invoice.phone_payment_available && invoice.phone_payment_unavailable_reason && (
          <p className="text-caption text-muted-foreground">
            {invoice.phone_payment_unavailable_reason}
          </p>
        )}
      </TabsContent>
      {invoice.phone_payment_available && (
        <TabsContent value="phone">
          {stripePromise ? (
            <Elements stripe={stripePromise}>
              <PhonePaymentFields
                pending={phonePending}
                error={phoneError}
                onSubmit={onSubmitPhone}
              />
            </Elements>
          ) : (
            <Alert>
              Phone Payment is not configured. Set the Stripe publishable key before taking card
              details.
            </Alert>
          )}
        </TabsContent>
      )}
    </Tabs>
  )
}

function PhonePaymentFields({
  pending,
  error,
  onSubmit,
}: {
  pending: boolean
  error: Error | null
  onSubmit: (form: PhonePaymentForm) => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [fieldError, setFieldError] = React.useState<string | null>(null)
  const idempotencyKey = React.useRef(paymentIdempotencyKey())

  React.useEffect(() => {
    if (error) idempotencyKey.current = paymentIdempotencyKey()
  }, [error])

  const submit = async () => {
    const card = elements?.getElement(CardElement)
    if (!stripe || !card) return

    setFieldError(null)
    const result = await stripe.createPaymentMethod({ type: 'card', card })
    if (result.error) {
      setFieldError(result.error.message ?? 'Card details could not be submitted.')
      return
    }

    onSubmit({ paymentMethodId: result.paymentMethod.id, idempotencyKey: idempotencyKey.current })
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <p className="text-body-sm font-medium text-foreground">Card details</p>
        <div className="rounded-md border border-border bg-background px-3 py-3">
          <CardElement options={{ hidePostalCode: false }} />
        </div>
      </div>
      <Alert variant="warning">
        Phone Payments skip payer authentication. The Association accepts the elevated dispute risk.
      </Alert>
      {(fieldError || error) && (
        <Alert variant="destructive">
          {fieldError ?? apiFieldMessages(error, 'phone_payment')[0] ?? 'Phone Payment failed'}
        </Alert>
      )}
      <div className="flex justify-end">
        <Button type="button" disabled={!stripe || pending} onClick={() => void submit()}>
          {pending ? 'Charging...' : 'Charge card by phone'}
        </Button>
      </div>
    </div>
  )
}

export function submitPaymentForm(contactId: number, form: PaymentForm) {
  return postJSON<unknown>(`/api/v1/contacts/${contactId}/payments`, {
    payment: {
      amount: form.amount,
      method: form.method,
      custom_payment_type_id: form.customPaymentTypeId,
      paid_on: form.paidOn,
      reference: form.reference,
      idempotency_key: form.idempotencyKey,
      allocations: form.allocations.map((allocation) => ({
        invoice_id: allocation.invoiceId,
        amount: allocation.amount,
      })),
    },
  })
}

export function submitPhonePaymentForm(invoiceId: number, form: PhonePaymentForm) {
  return postJSON<InvoiceResponse>(`/api/v1/invoices/${invoiceId}/phone_payments`, {
    phone_payment: {
      payment_method_id: form.paymentMethodId,
      idempotency_key: form.idempotencyKey,
    },
  })
}

function paymentIdempotencyKey() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()

  return `payment-${Date.now()}-${Math.random().toString(36).slice(2)}`
}
